// ---------------------------------------------------------------------------
// Pharmacy Ship-To Coverage — data & helpers
// ---------------------------------------------------------------------------
// Pharmacy roster comes from manufacturers with isPharmacy = true
// (GET /commonservice/getPharmacies). Coverage maps are built from
// pharmacy_licenses for each manufacturer's id.
// ---------------------------------------------------------------------------

import { getPharmaciesListSkipLoader } from "../../../service/CommonService";
import { getPharmacyLicensesByPharmacyId } from "../../../service/PharmacyLicenseService";
import type { PharmacyLicense } from "../../../utils/rxOrderGating";
import { isLicenseUnexpired, todayIsoDate } from "../../../utils/rxOrderGating";

export type CompoundType = "sterile" | "nonsterile";

export interface Pharmacy {
  id: string;
  name: string;
  /** Brand color used for the filter pill dot / legend. */
  color: string;
  /** manufacturer_id used to load pharmacy_licenses. */
  manufacturerId: number;
}

/** For each pharmacy, which states it can ship each compound type to. */
export interface CoverageEntry {
  sterile: string[];
  nonsterile: string[];
}

/** USPS code -> full state name (the 50 states + DC). */
export const STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

export const ALL_STATE_CODES: string[] = Object.keys(STATE_NAMES);
export const TOTAL_JURISDICTIONS = ALL_STATE_CODES.length;

/** FIPS state code (as used by us-atlas topojson) -> USPS abbreviation. */
export const FIPS_TO_CODE: Record<string, string> = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO",
  "09": "CT", "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI",
  "16": "ID", "17": "IL", "18": "IN", "19": "IA", "20": "KS", "21": "KY",
  "22": "LA", "23": "ME", "24": "MD", "25": "MA", "26": "MI", "27": "MN",
  "28": "MS", "29": "MO", "30": "MT", "31": "NE", "32": "NV", "33": "NH",
  "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND", "39": "OH",
  "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA",
  "54": "WV", "55": "WI", "56": "WY",
};

/** Palette assigned by roster order from getPharmacies. */
export const PHARMACY_COLORS = [
  "#f97316",
  "#6366f1",
  "#14b8a6",
  "#e11d48",
  "#0ea5e9",
  "#a855f7",
  "#84cc16",
  "#f59e0b",
];

export const EMPTY_COVERAGE: Record<string, CoverageEntry> = {};

type ManufacturerPharmacyRow = {
  manufacturerId?: number | null;
  manufacturerName?: string | null;
};

/** Map getPharmacies manufacturer rows onto the coverage roster. */
export function mapManufacturersToPharmacies(rows: ManufacturerPharmacyRow[] | null | undefined): Pharmacy[] {
  return (rows || [])
    .filter((row) => Number(row.manufacturerId) > 0)
    .map((row, index) => ({
      id: String(row.manufacturerId),
      name: String(row.manufacturerName || `Pharmacy ${row.manufacturerId}`).trim(),
      color: PHARMACY_COLORS[index % PHARMACY_COLORS.length],
      manufacturerId: Number(row.manufacturerId),
    }));
}

function normalizeState(state: string | null | undefined): string {
  return (state || "").trim().toUpperCase();
}

function isShipableLicense(license: PharmacyLicense, asOfDate: string): boolean {
  if (!license || license.status !== "active") return false;
  return isLicenseUnexpired(license.expiration_date, asOfDate);
}

/**
 * Build sterile / non-sterile state lists from pharmacy_licenses rows.
 * - Non-sterile: active + unexpired license for the state
 * - Sterile: same, plus sterile_authorized
 */
export function buildCoverageEntryFromLicenses(
  licenses: PharmacyLicense[],
  asOfDate: string = todayIsoDate(),
): CoverageEntry {
  const sterile = new Set<string>();
  const nonsterile = new Set<string>();

  (licenses || []).forEach((license) => {
    const code = normalizeState(license.state);
    if (!code || !STATE_NAMES[code]) return;
    if (!isShipableLicense(license, asOfDate)) return;

    nonsterile.add(code);
    if (license.sterile_authorized) {
      sterile.add(code);
    }
  });

  return {
    sterile: Array.from(sterile).sort(),
    nonsterile: Array.from(nonsterile).sort(),
  };
}

export function buildCoverageFromLicenses(
  pharmacies: Pharmacy[],
  licensesByPharmacyId: Record<string, PharmacyLicense[]>,
  asOfDate: string = todayIsoDate(),
): Record<string, CoverageEntry> {
  const coverage: Record<string, CoverageEntry> = {};
  pharmacies.forEach((ph) => {
    coverage[ph.id] = buildCoverageEntryFromLicenses(
      licensesByPharmacyId[ph.id] || [],
      asOfDate,
    );
  });
  return coverage;
}

// ---------------------------------------------------------------------------
// Helpers (pure — pass coverage / pharmacies from loaded state)
// ---------------------------------------------------------------------------

/** States a single pharmacy covers for a compound type. */
export function statesFor(
  pharmacyId: string,
  type: CompoundType,
  coverage: Record<string, CoverageEntry>,
): Set<string> {
  const entry = coverage[pharmacyId];
  return new Set(entry ? entry[type] : []);
}

/** Number of jurisdictions a single pharmacy covers for a compound type. */
export function pharmacyStateCount(
  pharmacyId: string,
  type: CompoundType,
  coverage: Record<string, CoverageEntry>,
): number {
  return coverage[pharmacyId]?.[type]?.length ?? 0;
}

/**
 * Resolve a coverage-map pharmacy filter.
 * Prefer manufacturerId (stable). Fall back to name match against a loaded roster.
 */
export function resolvePharmacyId(
  manufacturerName?: string | null,
  manufacturerId?: number | null,
  pharmacies: Pharmacy[] = [],
): string {
  if (manufacturerId && manufacturerId > 0) return String(manufacturerId);
  if (!manufacturerName) return "all";
  const needle = manufacturerName.trim().toLowerCase().replace(/\s+/g, "");
  if (!needle) return "all";
  const match = pharmacies.find((p) => {
    const name = p.name.toLowerCase().replace(/\s+/g, "");
    return needle === name || needle.includes(name) || name.includes(needle);
  });
  return match ? match.id : "all";
}

/**
 * For a given compound type and filter ("all" or a pharmacy id), returns a map
 * of stateCode -> list of pharmacy names covering it.
 */
export function coverageByState(
  type: CompoundType,
  filter: string,
  pharmacies: Pharmacy[],
  coverage: Record<string, CoverageEntry>,
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  const pool = filter === "all" ? pharmacies : pharmacies.filter((p) => p.id === filter);
  pool.forEach((ph) => {
    statesFor(ph.id, type, coverage).forEach((code) => {
      (result[code] ||= []).push(ph.name);
    });
  });
  return result;
}

/** How many of the 51 jurisdictions have at least one pharmacy covering them. */
export function jurisdictionsCovered(
  type: CompoundType,
  pharmacies: Pharmacy[],
  coverage: Record<string, CoverageEntry>,
): number {
  return Object.keys(coverageByState(type, "all", pharmacies, coverage)).length;
}

/**
 * Load isPharmacy manufacturers, then pharmacy_licenses for each, and build map coverage.
 */
export async function loadCoverage(): Promise<{
  pharmacies: Pharmacy[];
  coverage: Record<string, CoverageEntry>;
  licenses: PharmacyLicense[];
}> {
  const pharmaciesResponse = await getPharmaciesListSkipLoader({ "x-skip-loader": "true" });
  const pharmacies = mapManufacturersToPharmacies(pharmaciesResponse?.data?.data);
  const licenseGroups = await Promise.all(
    pharmacies.map(async (ph) => {
      const rows = await getPharmacyLicensesByPharmacyId(ph.manufacturerId);
      return { id: ph.id, rows };
    }),
  );

  const licensesByPharmacyId: Record<string, PharmacyLicense[]> = {};
  const licenses: PharmacyLicense[] = [];
  licenseGroups.forEach(({ id, rows }) => {
    licensesByPharmacyId[id] = rows;
    licenses.push(...rows);
  });

  const coverage = buildCoverageFromLicenses(pharmacies, licensesByPharmacyId);
  return { pharmacies, coverage, licenses };
}
